import { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import {
  ESdkEvents,
  EventParams,
  NuveiParamsType,
  EPaymentProvider,
  NuveiFinalizePaymentResponse,
  NuveiCardFinalizeRequestBody,
  GetNuveiPaymentStatusResponse,
  NuveiSaveCardFinalizeRequestBody,
  AppchargeErrorType,
} from '../../../contracts';
import {
  DEFAULT_ERROR_MESSAGE,
  getNuveiUserReadbleErrorMessage,
} from '../../../utils/get-nuvei-error-message';
import { useOrderPolling } from './use-order-polling';
import { usePaymentState } from './use-payment-state';
import { useAPI } from '../../../services/api.service';
import { openNewTab } from '../../../utils/open-new-tab';
import { useCheckout } from '../../../hooks/use-checkout';
import { getCheckoutUrl } from '../../../utils/get-checkout-url';
import { NuveiCardDetails } from '../components/NuveiPayment/types';
import { InternalMessageEventsName } from '../../../constants/enums';
import { sendFEEvent, sendInternalFEEvent } from '../../../eventUtil';
import { SavedCardFormProps } from '../components/SavedCards/SavedCards';
import { AddressForm } from '../components/AddressElement/AddressElement';
import { NuveiCardForm } from '../components/NuveiPayment/components/NuveiCard/NuveiCard';
import { AddressElementRef } from '../components/AddressElement/hooks/use-address-element-ref';
import { generateAppchargeErrorMessage } from '../../../utils/generate-appcharge-error-message';

const CARD_ERROR_3DS_NOT_SUPPORTED_MESSAGE = 'Card does not support 3D secure';

export function useNuveiSubmit() {
  const { API } = useAPI();
  const isNuveiPaymentInProgress = useRef(false);
  const { showBoundary } = useErrorBoundary();
  const { startOrderPolling } = useOrderPolling();
  const { selectedPaymentMethod, getEventParams, safeCharge, checkoutData } = useCheckout();
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const zipCode = usePaymentState((state) => state.zipCode);
  const fullAddress = usePaymentState((state) => state.fullAddress);
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  const handleCardPay = async (
    isFormValid: boolean,
    formValues: NuveiCardForm,
    cardObj: any,
    addressComponent?: AddressElementRef
  ) => {
    addressComponent?.validate();
    const isZipCodeError = addressComponent && !addressComponent.isValid();
    if (!isFormValid || isZipCodeError || isNuveiPaymentInProgress.current) {
      return;
    }
    isNuveiPaymentInProgress.current = true;
    setIsPaymentLoading(true);
    try {
      const data = await API.createNuveiSession(selectedPaymentMethod?.id!, formValues.email);
      const nuveiSessionToken = data.sessionToken;
      submit3DSCard(nuveiSessionToken, formValues, cardObj, addressComponent?.values);
    } catch (error) {
      handleError();
    }
  };

  const handleSavedCardPay = async (formValues: SavedCardFormProps) => {
    if (isNuveiPaymentInProgress.current) {
      return;
    }
    isNuveiPaymentInProgress.current = true;
    setIsPaymentLoading(true);
    const FEeventParams: Partial<EventParams> = {
      isSavedPaymentMethodUsed: true,
    };
    try {
      const data = await API.createNuveiSession(selectedPaymentMethod?.id!, formValues.email);
      const nuveiSessionToken = data.sessionToken;
      const savedCardFinalizeRequestBody = createSavedCardFinalizeRequestBody(
        formValues.email,
        nuveiSessionToken,
        formValues.cardID,
        selectedPaymentMethod?.paymentMethodDisplayName as string,
        selectedPaymentMethod?.id as string,
        formValues.securityCode
      );
      finalizePayment(savedCardFinalizeRequestBody, FEeventParams);
    } catch (error) {
      handleError();
    }
  };

  const handleAPMPay = async (
    formValues: Record<string, string>,
    isFormValid: boolean,
    addressComponent?: AddressElementRef
  ) => {
    const isZipCodeError = addressComponent && !addressComponent.isValid();
    if (!isFormValid || isZipCodeError || isNuveiPaymentInProgress.current) {
      return;
    }
    isNuveiPaymentInProgress.current = true;
    setIsPaymentLoading(true);
    try {
      const APMTab = openNewTab(`${getCheckoutUrl()}/apm-dynamic-page`);
      const waitForAPMTabLoad = new Promise((resolve, reject) => {
        if (APMTab) {
          APMTab.onload = function () {
            resolve('');
          };
        } else {
          reject('');
        }
      });
      const data = await API.createNuveiSession(selectedPaymentMethod?.id!, formValues.email);
      const nuveiSessionToken = data.sessionToken;
      submitApm(nuveiSessionToken, formValues, APMTab, waitForAPMTabLoad);
    } catch (error) {
      handleError();
    }
  };

  const submitApm = (
    nuveiSessionToken: string,
    formValues: Record<string, string>,
    APMTab: Window | null,
    waitForAPMTabLoad: Promise<any>
  ) => {
    const customer = checkoutData?.customer;
    const additionalInputFields = selectedPaymentMethod?.additionalInputFields || [];
    const alternativePaymentMethod = additionalInputFields.reduce((fields, currentField) => {
      const fieldName = currentField.key;
      const fieldValue = formValues[fieldName];
      if (fieldValue) {
        return { ...fields, [fieldName]: fieldValue };
      }
      return fields;
    }, {});

    const finalizePaymentRequestBody = {
      paymentProvider: EPaymentProvider.nuvei,
      receipt_email: formValues.email || customer?.email,
      params: {
        ...alternativePaymentMethod,
        sessionToken: nuveiSessionToken,
      },
      paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
      paymentMethodId: selectedPaymentMethod?.id,
    };

    API.finalizePayment<NuveiFinalizePaymentResponse>(finalizePaymentRequestBody)
      .then((finalizeResponse) => {
        waitForAPMTabLoad
          .then(() => {
            if (finalizeResponse.nuvei.status === 'redirect') {
              sendInternalFEEvent(
                APMTab,
                InternalMessageEventsName.APM_REDIRECT,
                finalizeResponse.nuvei.redirectUrl
              );
              handleRedirect(nuveiSessionToken, APMTab);
            } else if (finalizeResponse.nuvei.status === 'failed') {
              sendInternalFEEvent(APMTab, InternalMessageEventsName.APM_REDIRECT_CLOSE);
              handleError(finalizeResponse.appChargeErrorType);
              sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
                ...getEventParams(),
              });
            }
          })
          .catch(() => {
            handleError();
          });
      })
      .catch((error) => {
        sendInternalFEEvent(APMTab, InternalMessageEventsName.APM_REDIRECT_CLOSE);
        const errResponse = error?.response?.data;
        handleError();
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          ...getEventParams(),
          reason: errResponse?.message,
          error: {
            isFatal: true,
            reason: errResponse?.message,
          },
        });
      });
  };

  const handleRedirect = (nuveiSessionToken: string, APMTab: Window | null) => {
    const interval = setInterval(() => {
      if (APMTab?.closed) {
        clearInterval(interval);
        checkAPMStatus(nuveiSessionToken);
      }
    }, 1000);
  };

  const checkAPMStatus = async (nuveiSessionToken: string) => {
    try {
      const paymentStatusReponse = await API.getPaymentStatus<GetNuveiPaymentStatusResponse>(
        nuveiSessionToken
      );
      if (
        paymentStatusReponse.status === 'ERROR' ||
        paymentStatusReponse.transactionStatus === 'ERROR' ||
        paymentStatusReponse.transactionStatus === 'DECLINED'
      ) {
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          ...getEventParams(),
          reason: paymentStatusReponse.reason || paymentStatusReponse.gwErrorReason,
        });
        handleError();
      } else if (paymentStatusReponse.transactionStatus === 'APPROVED') {
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
          ...getEventParams(),
        });
        startOrderPolling(checkoutData!.orderId);
      } else if (paymentStatusReponse.transactionStatus === 'PENDING') {
        handleError();
      }
    } catch (error: any) {
      const errResponse = error?.response?.data;
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: errResponse.message,
      });
      handleError();
    }
  };

  const submit3DSCard = async (
    nuveiSessionToken: string,
    formValues: NuveiCardForm,
    cardObj: any,
    addressValues?: AddressForm
  ) => {
    const FEeventParams: Partial<EventParams> = {
      isNewPaymentMethodSaved: formValues.shouldSaveCard,
    };
    const cardDetails = await getCardDetails(nuveiSessionToken);
    safeCharge.authenticate3d(
      {
        sessionToken: nuveiSessionToken,
        cardHolderName: formValues.cardholderName,
        paymentOption: cardObj,
        billingAddress: {
          email: formValues.email,
          country: checkoutData?.customer.geoLocator.countryCode2!,
          address: addressValues?.address || fullAddress,
          zip: addressValues?.zipCode || zipCode,
        },
      },
      (response) => {
        const isNon3DSCard =
          response.result === 'ERROR' &&
          response.errorDescription === CARD_ERROR_3DS_NOT_SUPPORTED_MESSAGE;
        if (isNon3DSCard) {
          //submit non 3ds card
          submitCard(
            nuveiSessionToken,
            formValues,
            cardObj,
            FEeventParams,
            cardDetails,
            addressValues
          );
        } else if (
          response.status === 'ERROR' ||
          response.result === 'ERROR' ||
          response.result === 'DECLINED'
        ) {
          const errorMessage = getNuveiUserReadbleErrorMessage(
            response.gwErrorReason || response.reason
          );

          sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
            ...getEventParams(),
            reason: response.gwErrorReason || response.reason,
            ...FEeventParams,
          });
          handleError();
        } else if (response.result === 'APPROVED') {
          const ccTempToken = response.ccTempToken;
          const transactionId = response.transactionId;
          const cardFinalizeRequestBody: NuveiCardFinalizeRequestBody =
            createCardFinalizeRequestBody(
              formValues,
              nuveiSessionToken,
              ccTempToken,
              selectedPaymentMethod?.paymentMethodDisplayName as string,
              selectedPaymentMethod?.id as string,
              cardDetails,
              transactionId
            );
          finalizePayment(cardFinalizeRequestBody, FEeventParams);
        }
      }
    );
  };

  const submitCard = (
    nuveiSessionToken: string,
    formValues: NuveiCardForm,
    cardObj: any,
    FEeventParams: Partial<EventParams>,
    cardDetails?: NuveiCardDetails,
    addressValues?: AddressForm
  ) => {
    safeCharge
      .getToken(cardObj, {
        sessionToken: nuveiSessionToken,
        cardHolderName: formValues.cardholderName,
        billingAddress: {
          email: formValues.email,
          country: checkoutData?.customer.geoLocator.countryCode2!,
          address: addressValues?.address || checkoutData?.customer.address?.address || fullAddress,
          zip: addressValues?.zipCode || checkoutData?.customer.address?.zipCode || zipCode,
        },
      })
      .then((response) => {
        if (response.status === 'SUCCESS') {
          const ccTempToken = response.ccTempToken;
          const cardFinalizeRequestBody = createCardFinalizeRequestBody(
            formValues,
            nuveiSessionToken,
            ccTempToken,
            selectedPaymentMethod?.paymentMethodDisplayName as string,
            selectedPaymentMethod?.id as string,
            cardDetails
          );
          finalizePayment(cardFinalizeRequestBody, FEeventParams);
        } else if (response.status === 'ERROR') {
          const errorMessage = getNuveiUserReadbleErrorMessage(
            response.gwErrorReason || response.reason
          );
          sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
            ...getEventParams(),
            reason: response.gwErrorReason || response.reason,
            ...FEeventParams,
          });
          handleError();
        } else {
          handleError();
        }
      });
  };

  const getCardDetails = async (nuveiSessionToken: string): Promise<NuveiCardDetails> => {
    return new Promise((resolve) => {
      safeCharge.getCardDetails(
        {
          sessionToken: nuveiSessionToken,
          useFields: true,
        },
        resolve
      );
    });
  };

  const finalizePayment = async (body: any, FEeventParams?: Partial<EventParams>) => {
    try {
      const data = await API.finalizePayment<NuveiFinalizePaymentResponse>(body);
      handleServerResponse(data, FEeventParams);
    } catch (error: any) {
      const errResponse = error?.response?.data;
      showBoundary(t('unableToProcessPayment'));
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: errResponse?.message,
        ...FEeventParams,
        error: {
          isFatal: true,
          reason: errResponse?.message,
        },
      });
    }
  };

  const handleServerResponse = async (
    response: NuveiFinalizePaymentResponse,
    FEeventParams?: Partial<EventParams>
  ) => {
    if (response.paymentProvider === EPaymentProvider.nuvei) {
      if (response.nuvei.status === 'succeeded') {
        startOrderPolling(response.orderId, FEeventParams);
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
          ...getEventParams(),
          ...FEeventParams,
        });
      } else if (response.nuvei.status === 'pending') {
        startOrderPolling(response.orderId);
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
          ...getEventParams(),
          ...FEeventParams,
        });
      } else {
        handleError();
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          ...getEventParams(),
          ...FEeventParams,
          paymentMethodName: selectedPaymentMethod?.paymentMethodDisplayName,
        });
      }
    }
  };

  function handleError(appChargeErrorType?: AppchargeErrorType) {
    setIsPaymentLoading(false);
    isNuveiPaymentInProgress.current = false;
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    setErrorMessage(errorMessage);
  }

  return { handleCardPay, handleSavedCardPay, handleAPMPay };
}

function createCardFinalizeRequestBody(
  formValues: NuveiCardForm,
  nuveiSessionToken: string,
  ccTempToken: string,
  paymentMethodDisplayName: string,
  paymentMethodId: string,
  cardDetails?: NuveiCardDetails,
  transactionId?: string
): NuveiCardFinalizeRequestBody {
  return {
    paymentProvider: EPaymentProvider.nuvei,
    receipt_email: formValues.email,
    params: {
      sessionToken: nuveiSessionToken,
      pspIdentifier: ccTempToken,
      type: NuveiParamsType.ccTempToken,
      shouldSaveCard: formValues.shouldSaveCard,
      transactionId: transactionId,
      cardDetails: {
        bin: cardDetails?.bin || '',
        expirationMonth: cardDetails?.ccExpMonth || '',
        expirationYear: cardDetails?.ccExpYear || '',
        lastFourDigits: cardDetails?.last4digits || '',
        nameOnCard: formValues.cardholderName,
        cardBrand: cardDetails?.brand || '',
        cardType: cardDetails?.cardType || '',
        countryOfIssuance: cardDetails?.issuerCountry || '',
      },
    },
    paymentMethodDisplayName,
    paymentMethodId,
  };
}

function createSavedCardFinalizeRequestBody(
  email: string,
  nuveiSessionToken: string,
  pspCardIdentifier: string,
  paymentMethodDisplayName: string,
  paymentMethodId: string,
  cvv: string
): NuveiSaveCardFinalizeRequestBody {
  return {
    paymentProvider: EPaymentProvider.nuvei,
    receipt_email: email,
    params: {
      sessionToken: nuveiSessionToken,
      pspIdentifier: pspCardIdentifier,
      type: NuveiParamsType.userPaymentOptions,
      cvv,
    },
    paymentMethodDisplayName,
    paymentMethodId,
  };
}
